import { Button, Dialog, DialogContent, DialogTrigger, TextArea, TextField } from '@chatsdet/ui';
import { GithubLogo } from '@phosphor-icons/react';

import type { LinkedRepo } from './PropertyCodeTab.types';

interface PropertyCodeEditRepositoryDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  repository: LinkedRepo | null;
  rootDir: string;
  onRootDirChange: (rootDir: string) => void;
  agentHint: string;
  onAgentHintChange: (agentHint: string) => void;
  isPending: boolean;
  onCancel: () => void;
  onSave: () => void;
  /** Only orgs with the PR Review feature see the guidance field. */
  showPrReviewGuidance?: boolean;
  prReviewGuidance?: string;
  onPrReviewGuidanceChange?: (value: string) => void;
}

export function PropertyCodeEditRepositoryDialog({
  isOpen,
  onOpenChange,
  repository,
  rootDir,
  onRootDirChange,
  agentHint,
  onAgentHintChange,
  isPending,
  onCancel,
  onSave,
  showPrReviewGuidance = false,
  prReviewGuidance = '',
  onPrReviewGuidanceChange,
}: PropertyCodeEditRepositoryDialogProps) {
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <span />
      <Dialog
        eyebrow="Edit link"
        title="Edit Repository Settings"
        description="Update the monorepo hints used by the test planning agent for this repository."
        footer={
          <>
            <Button variant="ghost" onPress={onCancel} isDisabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={onSave}
              isDisabled={!repository || isPending}
            >
              {isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <DialogContent>
          {repository && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
                <GithubLogo
                  size={20}
                  weight="duotone"
                  className="shrink-0 text-muted-foreground"
                />
                <span className="truncate text-sm font-medium">{repository.repoFullName}</span>
              </div>
              <TextField
                label="Primary App Directory"
                value={rootDir}
                onChange={onRootDirChange}
                placeholder="/apps/web"
                description="Hint for test planning agent (e.g., /apps/web, /packages/frontend)"
              />
              <TextArea
                label="Agent Context Hint"
                value={agentHint}
                onChange={onAgentHintChange}
                placeholder="This is a monorepo. The web app depends on /packages/api for backend and /packages/shared for types."
                rows={3}
                description="Optional context about repo organization and related services"
              />
              {showPrReviewGuidance && (
                <TextArea
                  label="AI PR Review Guidance"
                  value={prReviewGuidance}
                  onChange={onPrReviewGuidanceChange}
                  placeholder="Flag anything touching payments or auth as high risk. Ignore lint-only and formatting-only diffs."
                  rows={3}
                  description="Optional markdown steering guidance for the AI PR reviewer on this repo — production regression risk to watch for, or noise to ignore."
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
